/**
 * Creators (iter-82 waitlist edition).
 *
 * Per the UX audit: the full Creator Studio surface is premature for MVP.
 * Replace this page with a "Coming Soon · Join Waitlist" tile that validates
 * demand BEFORE we invest engineering. Email captures persist to the existing
 * `creator_waitlist` collection (created here, no migration needed).
 *
 * The pre-iter-82 Creators.jsx had a public gallery + creator-profile system —
 * that's preserved in git history; we can restore it once the waitlist crosses
 * a meaningful threshold.
 */
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Check, ArrowRight } from "lucide-react";
import MobileTopBar from "../components/mobile/MobileTopBar";
import HamburgerSheet from "../components/mobile/HamburgerSheet";
import { Toaster } from "../components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
    { key: "creator", label: "creator" },
    { key: "business", label: "business" },
    { key: "community", label: "community" },
    { key: "other", label: "other" },
];

export default function Creators() {
    const [email, setEmail] = useState("");
    const [role, setRole] = useState("");
    const [useCase, setUseCase] = useState("");
    const [done, setDone] = useState(false);
    const [count, setCount] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [hamburgerOpen, setHamburgerOpen] = useState(false);

    useEffect(() => {
        // Best-effort: fetch the public waitlist count so visitors see momentum.
        axios.get(`${API}/creators/waitlist/count`)
            .then(({ data }) => setCount(data?.count))
            .catch(() => { /* silently — not critical */ });
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        const v = (email || "").trim().toLowerCase();
        if (!v || !v.includes("@")) {
            toast.error("enter a valid email");
            return;
        }
        if (!role) {
            toast.error("pick a role so we know who's lining up");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/creators/waitlist`, {
                email: v,
                role,
                use_case: (useCase || "").trim().slice(0, 280) || null,
            });
            setDone(true);
            setCount((c) => (typeof c === "number" ? c + 1 : c));
            toast.success("you're in. we'll be in touch.");
        } catch (err) {
            const msg = err?.response?.data?.detail || "couldn't sign up — try again";
            toast.error(typeof msg === "string" ? msg : "couldn't sign up");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-app text-app pb-20">
            <Toaster theme="dark" position="bottom-center" />
            <MobileTopBar
                onOpenMenu={() => setHamburgerOpen(true)}
                onOpenNotifications={() => window.location.assign("/me")}
            />
            <HamburgerSheet open={hamburgerOpen} onClose={() => setHamburgerOpen(false)} />

            <div className="max-w-md mx-auto px-5 pt-8">
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                >
                    <div className="text-[10px] tracking-[0.32em] uppercase text-amber-300/85 mb-3">
                        coming soon
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl text-app leading-tight">
                        Creators &<br />
                        <span className="italic font-serif">Business Tools</span>
                    </h1>
                    <p className="mt-4 font-serif italic text-app-2 text-base leading-relaxed">
                        signature backdrops · branded cards · audience reach · creator royalties · sponsored prompts.
                    </p>
                    <p className="mt-3 text-sm text-app-3 leading-relaxed">
                        we're building it for poets, pastors, coaches, comics, communities — anyone whose words deserve a stage. drop your email and we'll bring you in early.
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-8 rounded-2xl border border-amber-300/15 bg-amber-400/5 p-5"
                    data-testid="creators-waitlist-card"
                >
                    {done ? (
                        <div className="flex items-start gap-3" data-testid="creators-waitlist-done">
                            <div className="w-9 h-9 rounded-full bg-emerald-400/15 border border-emerald-300/30 flex items-center justify-center mt-0.5">
                                <Check className="w-4 h-4 text-emerald-300" strokeWidth={2} />
                            </div>
                            <div>
                                <div className="font-serif italic text-app text-base">you're on the list.</div>
                                <div className="text-xs text-app-3 mt-1">we'll email you when creator tools open. no spam — promise.</div>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="flex flex-col gap-3">
                            <label className="text-[10px] tracking-[0.32em] uppercase text-amber-200/85">
                                join the creator waitlist
                            </label>
                            <input
                                type="email"
                                inputMode="email"
                                autoComplete="email"
                                placeholder="your email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={submitting}
                                data-testid="creators-waitlist-email"
                                className="w-full bg-black/40 border border-white/12 rounded-xl px-4 py-3 text-app placeholder-app-3/60 focus:border-amber-300/55 focus:outline-none font-serif italic"
                            />
                            <div
                                className="flex flex-wrap gap-2 mt-1"
                                role="radiogroup"
                                aria-label="role"
                                data-testid="creators-waitlist-roles"
                            >
                                {ROLES.map((r) => {
                                    const active = role === r.key;
                                    return (
                                        <button
                                            type="button"
                                            key={r.key}
                                            disabled={submitting}
                                            onClick={() => setRole(r.key)}
                                            data-testid={`creators-waitlist-role-${r.key}`}
                                            aria-pressed={active}
                                            className={`px-3.5 py-1.5 rounded-full text-[11px] tracking-[0.18em] uppercase font-sans transition-all ${
                                                active
                                                    ? "bg-amber-400 text-black border border-amber-300"
                                                    : "bg-black/30 text-app-2 border border-white/12 hover:border-amber-300/45 hover:text-app"
                                            }`}
                                        >
                                            {r.label}
                                        </button>
                                    );
                                })}
                            </div>
                            <textarea
                                rows={3}
                                maxLength={280}
                                placeholder="what would you use it for? (optional)"
                                value={useCase}
                                onChange={(e) => setUseCase(e.target.value)}
                                disabled={submitting}
                                data-testid="creators-waitlist-usecase"
                                className="w-full bg-black/40 border border-white/12 rounded-xl px-4 py-3 text-app placeholder-app-3/60 focus:border-amber-300/55 focus:outline-none font-serif italic resize-none text-sm leading-relaxed"
                            />
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="creators-waitlist-submit"
                                className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black font-medium text-sm tracking-wide transition-colors"
                            >
                                {submitting ? "joining..." : "notify me"}
                                {!submitting && <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />}
                            </button>
                        </form>
                    )}
                    {typeof count === "number" && count > 0 && (
                        <div className="mt-4 pt-4 border-t border-amber-300/10 flex items-center gap-2 text-[11px] text-amber-200/70">
                            <Sparkles className="w-3 h-3" strokeWidth={1.8} />
                            <span><span className="text-amber-100 font-medium">{count.toLocaleString()}</span> creators already waiting</span>
                        </div>
                    )}
                </motion.div>

                <p className="mt-8 text-center text-[10px] tracking-[0.22em] uppercase text-app-3">
                    keep making cards · we'll let you know when the next door opens
                </p>
            </div>
        </div>
    );
}
